// Push Notification Service for PoliBot
// Handles browser push notifications for policy alerts

class PushNotificationService {
  constructor() {
    this.permission = 'default';
    this.supported = 'Notification' in window;
  }

  // Check if browser supports notifications
  isSupported() {
    return this.supported;
  }

  // Get current permission status
  getPermission() {
    if (!this.supported) return 'unsupported';
    return Notification.permission;
  }

  // Request notification permission
  async requestPermission() {
    if (!this.supported) {
      console.warn('Push notifications not supported in this browser');
      return 'unsupported';
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  // Send a push notification
  async sendNotification(title, options = {}) {
    if (!this.supported) {
      console.warn('Push notifications not supported');
      return false;
    }

    if (Notification.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return false;
    }

    try {
      const defaultOptions = {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        vibrate: [200, 100, 200],
        requireInteraction: false,
        silent: false,
        ...options
      };

      const notification = new Notification(title, defaultOptions);

      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        if (options.onClick) {
          options.onClick(event);
        }
        if (options.url) {
          window.location.href = options.url;
        }
        notification.close();
      };

      return true;
    } catch (error) {
      console.error('Error sending notification:', error);
      return false;
    }
  }

  // Send policy alert notification
  async sendPolicyAlert(policy, language = 'en') {
    const isSpanish = language === 'es';
    
    const title = isSpanish 
      ? `Nueva Alerta de Política: ${policy.title}`
      : `New Policy Alert: ${policy.title}`;
    
    const riskLabel = policy.risk_score >= 70 
      ? (isSpanish ? 'ALTO RIESGO' : 'HIGH RISK')
      : policy.risk_score >= 40
        ? (isSpanish ? 'RIESGO MEDIO' : 'MEDIUM RISK')
        : (isSpanish ? 'BAJO RIESGO' : 'LOW RISK');

    const body = isSpanish
      ? `${riskLabel} - ${policy.plain_language_summary_es || policy.description}`
      : `${riskLabel} - ${policy.plain_language_summary || policy.description}`;

    return this.sendNotification(title, {
      body: body.substring(0, 200) + (body.length > 200 ? '...' : ''),
      tag: `policy-${policy.id}`,
      url: `/policies/${policy.id}`,
      data: { policyId: policy.id },
      requireInteraction: policy.risk_score >= 70,
    });
  }

  // Send generic alert notification
  async sendAlert(alert, language = 'en') {
    const isSpanish = language === 'es';
    
    const title = isSpanish
      ? `Alerta de PoliBot: ${alert.title}`
      : `PoliBot Alert: ${alert.title}`;

    return this.sendNotification(title, {
      body: alert.message,
      tag: `alert-${alert.id}`,
      url: '/alerts',
      requireInteraction: alert.urgency === 'high',
    });
  }
}

// Create singleton instance
const pushNotificationService = new PushNotificationService();

export default pushNotificationService;
export { PushNotificationService };
